// Single-scattering atmosphere (Rayleigh + Mie + ozone absorption) baked into a small sky-view table.
// The table is indexed by view altitude and azimuth relative to the light source, so one table
// serves every viewing direction. It is rebuilt only when the light's altitude changes.
// Deep twilight (the light more than 6° down) has its own treatment: see "Deep twilight" below.

const RG = 6360e3; // ground radius, m
const RT = 6460e3; // top of atmosphere, m
const H_R = 8000;
const H_M = 1200;
const BETA_R = [5.802e-6, 13.558e-6, 33.1e-6];
const BETA_M_SCA = 3.996e-6;
const BETA_M_EXT = 4.4e-6;
// Ozone absorption. Hillaire's 680/550/440 nm values are (0.65, 1.881, 0.085)e-6; red and green are
// raised here because three wavelengths under-represent the broad Chappuis band, which otherwise
// leaves the dusk zenith magenta instead of the blue people actually see.
const BETA_O = [2.2e-6, 2.4e-6, 0.12e-6];
const G_MIE = 0.8;
const OBS_H = 50; // observer height, m

export const LUT_W = 64; // azimuth from the light, 0..π
export const LUT_H = 48; // view altitude
export const ALT_MIN = -0.2; // radians

/**
 * The natural night sky as the dark-adapted eye takes it in (airglow and starlight, about 21.5 mag/arcsec² overhead and
 * brighter lower down: some 3e-4 cd/m²) in these tables' units: its ratio to the zenith of air this clear with the Sun
 * 45° up (about 2,000 cd/m², the CIE clear sky) times that table's zenith radiance (0.0117). The Sun's twilight is shown
 * no brighter than against this sky (engine.ts), so its last light fades into the night instead of being shown ever
 * brighter as the eye adapts to an ever darker zenith.
 */
export const NIGHT_SKY = 2e-9;

/** Texture v coordinate for an altitude (radians); denser near the horizon. */
export function altToV(alt: number) {
  return Math.sqrt(Math.max(0, Math.min(1, (alt - ALT_MIN) / (Math.PI / 2 - ALT_MIN))));
}
function vToAlt(v: number) {
  return ALT_MIN + v * v * (Math.PI / 2 - ALT_MIN);
}

function density(h: number) {
  const r = Math.exp(-h / H_R);
  const m = Math.exp(-h / H_M);
  const o = Math.max(0, 1 - Math.abs(h - 25000) / 15000);
  return [r, m, o];
}

/** Distance along a ray from radius r with direction cosine mu to the sphere of radius R (or −1). */
function raySphere(r: number, mu: number, R: number): number {
  const b = r * mu;
  const c = r * r - R * R;
  const disc = b * b - c;
  if (disc < 0) return -1;
  const s = Math.sqrt(disc);
  const t1 = -b - s, t2 = -b + s;
  if (t1 > 0) return t1;
  if (t2 > 0) return t2;
  return -1;
}

/** Transmittance from a point (radius r) towards a direction with cosine mu relative to local up. */
function transmittanceToLight(r: number, mu: number, out: number[], steps = 8) {
  // Blocked by the planet?
  const b = r * mu;
  const c = r * r - RG * RG;
  if (mu < 0 && b * b - c >= 0) {
    out[0] = out[1] = out[2] = 0;
    return;
  }
  const dist = raySphere(r, mu, RT);
  const ds = dist / steps;
  let odR = 0, odM = 0, odO = 0;
  for (let i = 0; i < steps; i++) {
    const t = (i + 0.5) * ds;
    const ri = Math.sqrt(r * r + t * t + 2 * r * t * mu);
    const [dr, dm, dO] = density(ri - RG);
    odR += dr * ds;
    odM += dm * ds;
    odO += dO * ds;
  }
  for (let k = 0; k < 3; k++) out[k] = Math.exp(-(BETA_R[k] * odR + BETA_M_EXT * odM + BETA_O[k] * odO));
}

const phaseR = (mu: number) => (3 / (16 * Math.PI)) * (1 + mu * mu);
function phaseM(mu: number) {
  const g = G_MIE, g2 = g * g;
  return ((3 / (8 * Math.PI)) * ((1 - g2) * (1 + mu * mu))) / ((2 + g2) * Math.pow(1 + g2 - 2 * g * mu, 1.5));
}

// ---------- Deep twilight ----------
// Once the light is more than 6° down, the air near the observer lies in the Earth's shadow and only a thin layer far
// off and high up is still sunlit. The general march below misses that layer between its widely spaced far samples,
// so the glow towards the set Sun switched on and off row by row and was gone below 18.5°. Worse, its
// multiple-scattering fill is a fraction of the table's mean radiance, which by then is that thin glow alone:
// measured against a second-order scattering reference, the fill holds to within a factor of two down to 12° and
// then collapses (2.5 times too dark at 15°, 30 times at 18°), leaving towards the Sun only single-scattered light
// that has grazed the Earth and crossed the whole horizon air, whose green and blue are all but gone. So in deep
// twilight single scattering is integrated over each ray's sunlit stretch, and the part of the multiple scattering
// that depends on the direction to the Sun, which the fill cannot hold, is computed from where it is made.
const DEEP_FROM = (-6 * Math.PI) / 180; // deep twilight below this...
const DEEP_FULL = (-12 * Math.PI) / 180; // ...with its multiple scattering fully in from here

/** The stretch [t0, t1] of the ray p + t·w (Earth-centred, w of unit length) inside the Earth's shadow, a cylinder
 * behind the planet away from the light direction L; null if it misses. */
function shadowSpan(p: number[], w: number[], L: number[]): [number, number] | null {
  const pl = p[0] * L[0] + p[1] * L[1] + p[2] * L[2];
  const wl = w[0] * L[0] + w[1] * L[1] + w[2] * L[2];
  const pw = p[0] * w[0] + p[1] * w[1] + p[2] * w[2];
  // Squared distance from the shadow's axis, minus RG²: a·t² + 2b·t + c (inside where negative).
  const a = 1 - wl * wl, b = pw - pl * wl, c = p[0] * p[0] + p[1] * p[1] + p[2] * p[2] - pl * pl - RG * RG;
  let t0 = -Infinity, t1 = Infinity;
  if (a > 1e-12) {
    const disc = b * b - a * c;
    if (disc <= 0) return null;
    const s = Math.sqrt(disc);
    t0 = (-b - s) / a;
    t1 = (-b + s) / a;
  } else if (c >= 0) return null;
  // Behind the planet: p·L + t·(w·L) < 0.
  if (wl > 0) t1 = Math.min(t1, -pl / wl);
  else if (wl < 0) t0 = Math.max(t0, -pl / wl);
  else if (pl >= 0) return null;
  return t1 > t0 ? [t0, t1] : null;
}

/**
 * Single-scattered light reaching p (Earth-centred) from direction w, lit from L: n samples over the sunlit stretch
 * of the ray (denser at its near end), after the optical depth of any shadowed stretch in front of it.
 */
function scatterSunlit(p: number[], w: number[], L: number[], n: number, tSteps: number, out: number[]) {
  out[0] = out[1] = out[2] = 0;
  const r = Math.hypot(p[0], p[1], p[2]);
  const mu = (p[0] * w[0] + p[1] * w[1] + p[2] * w[2]) / r;
  let dist = raySphere(r, mu, RG);
  if (dist < 0) dist = raySphere(r, mu, RT);
  else dist = Math.min(dist, 60e3); // looking down: stop at the ground
  if (dist <= 0) return;
  let t0 = 0, t1 = dist;
  const sh = shadowSpan(p, w, L);
  if (sh && sh[0] <= 0) t0 = sh[1]; // in the shadow first, then sunlit
  else if (sh && sh[0] < dist) t1 = sh[0]; // sunlit, then into the shadow
  if (t0 >= t1) return;
  let odR = 0, odM = 0, odO = 0, tPrev = 0;
  for (let s = 0; s < 8 && t0 > 0; s++) {
    const f = (s + 1) / 8, t = t0 * f * f, ds = t - tPrev, tm = (t + tPrev) / 2;
    tPrev = t;
    const [dr, dm, dO] = density(Math.sqrt(r * r + tm * tm + 2 * r * tm * mu) - RG);
    odR += dr * ds;
    odM += dm * ds;
    odO += dO * ds;
  }
  const nu = w[0] * L[0] + w[1] * L[1] + w[2] * L[2];
  const pr = phaseR(nu), pm = phaseM(nu), span = t1 - t0, tr = [0, 0, 0];
  tPrev = 0;
  for (let s = 0; s < n; s++) {
    const f = (s + 1) / n, t = span * f * f, ds = t - tPrev, tm = t0 + (t + tPrev) / 2;
    tPrev = t;
    const qx = p[0] + w[0] * tm, qy = p[1] + w[1] * tm, qz = p[2] + w[2] * tm;
    const ri = Math.sqrt(qx * qx + qy * qy + qz * qz);
    const [dr, dm, dO] = density(ri - RG);
    odR += dr * ds;
    odM += dm * ds;
    odO += dO * ds;
    transmittanceToLight(ri, (qx * L[0] + qy * L[1] + qz * L[2]) / ri, tr, tSteps);
    for (let k = 0; k < 3; k++) {
      const tv = Math.exp(-(BETA_R[k] * odR + BETA_M_EXT * odM + BETA_O[k] * odO));
      out[k] += tv * tr[k] * (BETA_R[k] * pr * dr + BETA_M_SCA * pm * dm) * ds;
    }
  }
}

// The source of multiple scattering: the mean single-scattered light reaching a parcel of air from all directions,
// by its height and the Sun's elevation there (natural logarithms, per channel). It does not depend on where the
// Sun is for the observer, so it is shared by every table, and a column (one elevation) is built when first needed.
const MS_H = [0, 4000, 10000, 20000, 35000, 55000]; // m
const MS_E0 = -30, MS_DE = 3, MS_NE = 13; // the Sun's elevation at the parcel, degrees: -30…+6
const msColumns: (Float64Array | undefined)[] = [];
/** Directions over the sphere, closer together just above the horizon where the twilight is seen, as [x, y, z, solid
 * angle] with y ≥ 0 (the light is mirror-symmetric about the Sun's vertical, so each counts twice). */
const MS_DIRS: number[][] = (() => {
  const el = [-90, -6, -1.5, 0, 0.8, 2, 4.5, 9, 18, 35, 90], az = [0, 18, 40, 70, 115, 180];
  const dirs: number[][] = [];
  const rad = Math.PI / 180;
  for (let i = 0; i + 1 < el.length; i++) {
    const s0 = Math.sin(el[i] * rad), s1 = Math.sin(el[i + 1] * rad), z = (s0 + s1) / 2, c = Math.sqrt(1 - z * z);
    for (let j = 0; j + 1 < az.length; j++) {
      const ph = ((az[j] + az[j + 1]) / 2) * rad;
      dirs.push([c * Math.cos(ph), c * Math.sin(ph), z, 2 * (az[j + 1] - az[j]) * rad * (s1 - s0)]);
    }
  }
  return dirs;
})();

function msColumn(n: number): Float64Array {
  const done = msColumns[n];
  if (done) return done;
  const col = new Float64Array(MS_H.length * 3);
  const e = ((MS_E0 + MS_DE * n) * Math.PI) / 180;
  const L = [Math.cos(e), 0, Math.sin(e)], I = [0, 0, 0];
  for (let h = 0; h < MS_H.length; h++) {
    const p = [0, 0, RG + Math.max(MS_H[h], OBS_H)], acc = [0, 0, 0];
    for (const d of MS_DIRS) {
      scatterSunlit(p, d, L, 8, 6, I);
      for (let k = 0; k < 3; k++) acc[k] += I[k] * d[3];
    }
    for (let k = 0; k < 3; k++) col[h * 3 + k] = Math.log(Math.max(acc[k] / (4 * Math.PI), 1e-30));
  }
  return (msColumns[n] = col);
}

/** The multiple-scattering source at height h (m) with the Sun at elevation e (radians) there. */
function msSource(h: number, e: number, out: number[]) {
  const fe = Math.min(MS_NE - 1, Math.max(0, ((e * 180) / Math.PI - MS_E0) / MS_DE));
  const n = Math.min(MS_NE - 2, Math.floor(fe)), te = fe - n;
  let k = 0;
  while (k < MS_H.length - 2 && h > MS_H[k + 1]) k++;
  const th = Math.min(1, Math.max(0, (h - MS_H[k]) / (MS_H[k + 1] - MS_H[k])));
  const a = msColumn(n), b = msColumn(n + 1);
  for (let c = 0; c < 3; c++) {
    const lo = a[k * 3 + c] + (b[k * 3 + c] - a[k * 3 + c]) * te;
    const hi = a[(k + 1) * 3 + c] + (b[(k + 1) * 3 + c] - a[(k + 1) * 3 + c]) * te;
    out[c] = Math.exp(lo + (hi - lo) * th);
  }
}

/** Light scattered a second time (evenly) towards the observer along d, from the source above, lit from L. */
function scatterMultiple(d: number[], L: number[], out: number[]) {
  out[0] = out[1] = out[2] = 0;
  const r0 = RG + OBS_H;
  let dist = raySphere(r0, d[2], RG);
  if (dist < 0) dist = raySphere(r0, d[2], RT);
  else dist = Math.min(dist, 60e3);
  let odR = 0, odM = 0, odO = 0, tPrev = 0;
  const j = [0, 0, 0];
  for (let s = 0; s < 10; s++) {
    const f = (s + 1) / 10, t = dist * f * f, ds = t - tPrev, tm = (t + tPrev) / 2;
    tPrev = t;
    const px = d[0] * tm, py = d[1] * tm, pz = r0 + d[2] * tm;
    const ri = Math.sqrt(px * px + py * py + pz * pz);
    const [dr, dm, dO] = density(ri - RG);
    odR += dr * ds;
    odM += dm * ds;
    odO += dO * ds;
    msSource(ri - RG, Math.asin(Math.max(-1, Math.min(1, (px * L[0] + py * L[1] + pz * L[2]) / ri))), j);
    for (let k = 0; k < 3; k++) {
      out[k] += Math.exp(-(BETA_R[k] * odR + BETA_M_EXT * odM + BETA_O[k] * odO)) * (BETA_R[k] * dr + BETA_M_SCA * dm) * j[k] * ds;
    }
  }
}

/**
 * Sky radiance table for a light source at altitude `lightAlt` (radians), for unit irradiance.
 * Returns RGB floats, LUT_W × LUT_H, row = altitude index.
 */
export function buildSkyTable(lightAlt: number): Float32Array {
  const out = new Float32Array(LUT_W * LUT_H * 4);
  const L = [Math.cos(lightAlt), 0, Math.sin(lightAlt)];
  const r0 = RG + OBS_H;
  const tr = [0, 0, 0];
  const steps = 20;
  const deep = lightAlt < DEEP_FROM - 1e-9; // (the -6° table itself stays as it always was)
  let sum = 0;
  for (let j = 0; j < LUT_H; j++) {
    const alt = vToAlt((j + 0.5) / LUT_H);
    const ca = Math.cos(alt), sa = Math.sin(alt);
    for (let i = 0; i < LUT_W; i++) {
      const az = ((i + 0.5) / LUT_W) * Math.PI;
      const d = [ca * Math.cos(az), ca * Math.sin(az), sa];
      const nu = d[0] * L[0] + d[1] * L[1] + d[2] * L[2];
      let dist = raySphere(r0, sa, RG);
      if (dist < 0) dist = raySphere(r0, sa, RT);
      else dist = Math.min(dist, 60e3); // looking down: stop at the ground
      let odR = 0, odM = 0, odO = 0;
      const acc = [0, 0, 0];
      if (deep) scatterSunlit([0, 0, r0], d, L, 10, 8, acc);
      else {
        // Exponentially spaced samples: fine near the observer where the air is dense.
        let tPrev = 0;
        for (let s = 0; s < steps; s++) {
          const f = (s + 1) / steps;
          const t = dist * f * f;
          const ds = t - tPrev;
          const tm = (t + tPrev) / 2;
          tPrev = t;
          // Sample position (observer at (0,0,r0)).
          const px = d[0] * tm, py = d[1] * tm, pz = r0 + d[2] * tm;
          const ri = Math.sqrt(px * px + py * py + pz * pz);
          const up = [px / ri, py / ri, pz / ri];
          const [dr, dm, dO] = density(ri - RG);
          odR += dr * ds;
          odM += dm * ds;
          odO += dO * ds;
          const muL = up[0] * L[0] + up[1] * L[1] + up[2] * L[2];
          transmittanceToLight(ri, muL, tr);
          if (tr[0] + tr[1] + tr[2] === 0) continue;
          const pr = phaseR(nu) * dr, pm = phaseM(nu) * dm;
          for (let k = 0; k < 3; k++) {
            const tv = Math.exp(-(BETA_R[k] * odR + BETA_M_EXT * odM + BETA_O[k] * odO));
            acc[k] += tv * tr[k] * (BETA_R[k] * pr + BETA_M_SCA * pm) * ds;
          }
        }
      }
      const o = (j * LUT_W + i) * 4;
      out[o] = acc[0];
      out[o + 1] = acc[1];
      out[o + 2] = acc[2];
      out[o + 3] = 1;
      sum += acc[1];
    }
  }
  // Crude multiple-scattering fill: a fraction of the mean radiance, bluish, everywhere above the horizon.
  const mean = sum / (LUT_W * LUT_H);
  for (let j = 0; j < LUT_H; j++) {
    const alt = vToAlt((j + 0.5) / LUT_H);
    const w = alt > 0 ? 1 : Math.max(0, 1 + alt * 10);
    for (let i = 0; i < LUT_W; i++) {
      const o = (j * LUT_W + i) * 4;
      out[o] += mean * 0.35 * 0.55 * w;
      out[o + 1] += mean * 0.35 * 0.8 * w;
      out[o + 2] += mean * 0.35 * 1.25 * w;
    }
  }
  if (deep) {
    // In deep twilight the fill stands for the even part of the multiple scattering; add the part that depends on
    // the direction to the Sun: what each ray gathers beyond what the same altitude gathers facing away from it.
    // Brought in smoothly between 6° and 12° down, where the fill still agrees with the second-order reference. It
    // changes slowly with azimuth, so it is worked out for every other column and the ones between interpolated.
    const f = Math.min(1, (DEEP_FROM - lightAlt) / (DEEP_FROM - DEEP_FULL)), k = f * f * (3 - 2 * f);
    const ms = [0, 0, 0], away = [0, 0, 0], row = new Float64Array(LUT_W * 3);
    for (let j = 0; j < LUT_H; j++) {
      const alt = vToAlt((j + 0.5) / LUT_H);
      const w = k * (alt > 0 ? 1 : Math.max(0, 1 + alt * 10));
      if (w === 0) continue;
      const ca = Math.cos(alt), sa = Math.sin(alt);
      scatterMultiple([-ca, 0, sa], L, away);
      for (let i = 0; i < LUT_W; i += i < LUT_W - 2 ? 2 : 1) {
        const az = ((i + 0.5) / LUT_W) * Math.PI;
        scatterMultiple([ca * Math.cos(az), ca * Math.sin(az), sa], L, ms);
        for (let c = 0; c < 3; c++) row[i * 3 + c] = w * Math.max(0, ms[c] - away[c]);
      }
      for (let i = 1; i < LUT_W - 2; i += 2) for (let c = 0; c < 3; c++) row[i * 3 + c] = (row[(i - 1) * 3 + c] + row[(i + 1) * 3 + c]) / 2;
      for (let i = 0; i < LUT_W; i++) for (let c = 0; c < 3; c++) out[(j * LUT_W + i) * 4 + c] += row[i * 3 + c];
    }
  }
  return out;
}

/** RGB transmittance of direct light from a source at altitude `alt` (radians) reaching the observer. */
export function directTransmittance(alt: number): [number, number, number] {
  const out = [0, 0, 0];
  transmittanceToLight(RG + OBS_H, Math.sin(Math.max(alt, -0.02)), out);
  return [out[0], out[1], out[2]];
}
